// src/catalog/pages/AppCatalogEntryDetailPage.jsx

import { useEffect, useState } from "react";
import {
  Navigate,
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/authContext";
import ContactFormModal from "../../components/modals/web-modals/ContactFormModal";
import {
  ApplicationButton,
  ApplicationContextualHelp,
  ApplicationPage,
  ApplicationSectionHeader,
  ApplicationStateRegion,
  ApplicationViewHeader
} from "../../components/application";
import CatalogEntryAdaptationSelector from "../components/detail/CatalogEntryAdaptationSelector";
import CatalogEntryLaunchConfigurationSelector from "../components/detail/CatalogEntryLaunchConfigurationSelector";
import CatalogEntryContactPanel from "../components/detail/CatalogEntryContactPanel";
import CatalogEntryHero from "../components/detail/CatalogEntryHero";
import CatalogEntryInformationPanels from "../components/detail/CatalogEntryInformationPanels";
import CatalogEntryLinksPanel from "../components/detail/CatalogEntryLinksPanel";
import CatalogEntryProgramLocaleSelector from "../components/detail/CatalogEntryProgramLocaleSelector";
import CatalogEntryRelatedEntriesPanel from "../components/detail/CatalogEntryRelatedEntriesPanel";
import {
  canonicalCatalogDetailRouteForLegacySlug,
  catalogPathwayCompatibilityForLegacyCatalogSlug
} from "../launch/catalogPathwayCompatibility";
import {
  getAvailableLaunchConfigurations,
  getPreferredLaunchConfigurationIdForEntry,
  getSelfDirectedLaunchConfigurations,
  resolveLaunchConfigurationSelectionFromList
} from "../launch/catalogLaunchConfigurations";
import {
  ProgramScopedLocaleProvider,
  useProgramScopedLocale
} from "../../features/program-localization/ProgramScopedLocaleContext";
import {
  getProgramScopedLocaleConfigForCatalogEntry
} from "../../features/program-localization/programScopedLocaleConfig";
import useCatalogEntry from "../hooks/useCatalogEntry";
import {
  buildCatalogContactMessage,
  getCatalogEntryPrimaryAction,
  isProjectAccessCodeAction,
  isProjectAccessCodeEnrollmentEntry,
  isCatalogDiscoveryAction,
  isCatalogContactAction
} from "../utils/catalogAccess";
import { openCatalogLaunch } from "../utils/catalogLaunch";
import {
  getContactEmail,
  getEntryDescription,
  getEntryTitle,
  getExternalLinks
} from "../utils/catalogDisplay";
import {
  buildCatalogEntryHeroMetaItems,
  getCatalogEntryDetailProfile
} from "../components/detail/catalogDetailProfiles";
import { getDefaultCAPEVAdaptationId } from "../../activities/cape-v/capevAdaptations";
import JoinProjectWithCode from "../../layouts/components/dashboard/JoinProjectWithCode";

const MODERATE_SESSION_LAUNCH_CONFIGURATION_ID =
  "moderate-session";

export default function AppCatalogEntryDetailPage() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const auth = useAuth();
  const [selectedCAPEVAdaptationId, setSelectedCAPEVAdaptationId] =
    useState("en");
  const [catalogContactRequest, setCatalogContactRequest] =
    useState(null);
  const legacyCompatibility =
    catalogPathwayCompatibilityForLegacyCatalogSlug(slug);
  const effectiveSlug =
    legacyCompatibility?.canonicalSlug || slug;

  const {
    entry,
    loading,
    error
  } = useCatalogEntry(effectiveSlug);

  const defaultCAPEVAdaptationId = getDefaultCAPEVAdaptationId(entry);

  useEffect(() => {
    if (entry) {
      setSelectedCAPEVAdaptationId(defaultCAPEVAdaptationId);
    }
  }, [entry, defaultCAPEVAdaptationId]);

  if (loading) {
    return (
      <ApplicationPage
        ariaLabelledby="catalog-entry-loading-title"
        className="catalog-page app-catalog-page"
        variant="discovery"
      >
        <ApplicationViewHeader
          title="Catalog entry"
          titleId="catalog-entry-loading-title"
          contextLabel="All-Voiced Catalog"
        />
        <ApplicationStateRegion
          status="loading"
          message={t("loading", { defaultValue: "Loading..." })}
        />
      </ApplicationPage>
    );
  }

  if (error) {
    return (
      <ApplicationPage
        ariaLabelledby="catalog-entry-error-title"
        className="catalog-page app-catalog-page"
        variant="discovery"
      >
        <ApplicationViewHeader
          title="Catalog entry"
          titleId="catalog-entry-error-title"
          contextLabel="All-Voiced Catalog"
        />
        <ApplicationStateRegion status="error" message={error} />
      </ApplicationPage>
    );
  }

  if (!entry) {
    return (
      <ApplicationPage
        ariaLabelledby="catalog-entry-not-found-title"
        className="catalog-page app-catalog-page"
        variant="discovery"
      >
        <ApplicationViewHeader
          title="Catalog entry not found"
          titleId="catalog-entry-not-found-title"
          contextLabel="All-Voiced Catalog"
        />
        <ApplicationStateRegion
          status="empty"
          message="This catalog entry may be unavailable, unpublished, or not included in the current catalog view."
        />
        <ApplicationButton
          variant="secondary"
          className="catalog-detail__secondary-button"
          onClick={() => navigate("/app/catalog")}
        >
          Back to catalog
        </ApplicationButton>
      </ApplicationPage>
    );
  }

  const programScopedLocaleConfig =
    getProgramScopedLocaleConfigForCatalogEntry(entry);

  if (legacyCompatibility) {
    const targetParams =
      new URLSearchParams(location.search);

    targetParams.set(
      "launchConfig",
      legacyCompatibility.launchConfigurationId
    );

    return (
      <Navigate
        replace
        to={{
          pathname: canonicalCatalogDetailRouteForLegacySlug(slug)
            ?.split("?")[0],
          search: `?${targetParams.toString()}`,
          hash: location.hash
        }}
      />
    );
  }

  return (
    <ProgramScopedLocaleProvider
      projectSlug={programScopedLocaleConfig?.projectSlug}
      programSlug={programScopedLocaleConfig?.programSlug}
      userCommunicationLanguage={auth.user?.communicationLanguage}
    >
      <CatalogEntryDetailContent
        catalogContactRequest={catalogContactRequest}
        entry={entry}
        navigate={navigate}
        searchParams={searchParams}
        selectedCAPEVAdaptationId={selectedCAPEVAdaptationId}
        setSearchParams={setSearchParams}
        setCatalogContactRequest={setCatalogContactRequest}
        setSelectedCAPEVAdaptationId={setSelectedCAPEVAdaptationId}
        userRole={auth.user?.role}
      />
    </ProgramScopedLocaleProvider>
  );
}

function CatalogEntryDetailContent({
  catalogContactRequest,
  entry,
  navigate,
  searchParams,
  selectedCAPEVAdaptationId,
  setSearchParams,
  setCatalogContactRequest,
  setSelectedCAPEVAdaptationId,
  userRole
}) {
  const { t } = useTranslation();
  const scopedLocale = useProgramScopedLocale();
  const entryT = scopedLocale.isEnabled
    ? scopedLocale.programT
    : t;
  const title = getEntryTitle(entry, entryT);
  const description = getEntryDescription(entry, entryT);
  const contactEmail = getContactEmail(entry);
  const externalLinks = getExternalLinks(entry, "app");
  const relatedEntries = entry?.relatedEntries || [];
  const primaryAction = getCatalogEntryPrimaryAction(entry);
  const detailProfile = getCatalogEntryDetailProfile(entry);
  const requestedLaunchConfigurationId =
    searchParams.get("launchConfig");
  const preferredLaunchConfigurationId =
    getPreferredLaunchConfigurationIdForEntry(entry);
  const selfDirectedConfigurations =
    getSelfDirectedLaunchConfigurations(entry, {
      userRole,
      respectAudience: false
    });
  const contextualFacilitationConfiguration =
    getAvailableLaunchConfigurations(entry, {
      userRole
    }).find(
      (configuration) =>
        configuration?.id ===
        MODERATE_SESSION_LAUNCH_CONFIGURATION_ID
    ) || null;
  const requestedOrdinaryLaunchConfigurationId =
    selfDirectedConfigurations.some(
      (configuration) =>
        configuration?.id ===
        requestedLaunchConfigurationId
    )
      ? requestedLaunchConfigurationId
      : null;
  const preferredOrdinaryLaunchConfigurationId =
    selfDirectedConfigurations.some(
      (configuration) =>
        configuration?.id ===
        preferredLaunchConfigurationId
    )
      ? preferredLaunchConfigurationId
      : null;
  const {
    selectedConfiguration: selectedOrdinaryConfiguration
  } = resolveLaunchConfigurationSelectionFromList(
    selfDirectedConfigurations,
    requestedOrdinaryLaunchConfigurationId,
    {
      preferredLaunchConfigurationId:
        preferredOrdinaryLaunchConfigurationId
    }
  );

  useEffect(() => {
    if (
      requestedLaunchConfigurationId &&
      !selfDirectedConfigurations.some(
        (configuration) =>
          configuration?.id === requestedLaunchConfigurationId
      )
    ) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("launchConfig");
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    selfDirectedConfigurations,
    requestedLaunchConfigurationId,
    searchParams,
    setSearchParams
  ]);

  const handlePrimaryAction = () => {
    if (!entry || primaryAction.disabled) return;

    if (isCatalogDiscoveryAction(primaryAction)) {
      navigate(primaryAction.to);
      return;
    }

    if (isCatalogContactAction(primaryAction)) {
      setCatalogContactRequest({
        entry,
        action: primaryAction
      });
      return;
    }

    if (isProjectAccessCodeAction(primaryAction)) {
      document
        .getElementById("catalog-project-access-code")
        ?.scrollIntoView?.({ behavior: "smooth", block: "start" });
      document
        .getElementById("catalog-project-access-code-input")
        ?.focus();
      return;
    }

    const launched = openCatalogLaunch(entry, navigate, {
      adaptationId: selectedCAPEVAdaptationId,
      launchConfigurationId:
        selectedOrdinaryConfiguration?.id,
      programLocale: scopedLocale.isEnabled
        ? scopedLocale.activeLocale
        : undefined
    });

    if (!launched) {
      navigate(`/app/catalog/${entry.slug}`);
    }
  };

  const handleFacilitateActivity = () => {
    if (!contextualFacilitationConfiguration) {
      return;
    }

    openCatalogLaunch(entry, navigate, {
      adaptationId: selectedCAPEVAdaptationId,
      launchConfigurationId:
        contextualFacilitationConfiguration.id,
      programLocale: scopedLocale.isEnabled
        ? scopedLocale.activeLocale
        : undefined
    });
  };

  return (
    <ApplicationPage
      ariaLabelledby="catalog-entry-title"
      className="catalog-page app-catalog-page catalog-detail"
      variant="discovery"
    >
      <ApplicationButton
        variant="secondary"
        className="catalog-detail__back"
        onClick={() => navigate("/app/catalog")}
      >
        ← Back to catalog
      </ApplicationButton>

      <CatalogEntryHero
        entry={entry}
        title={title}
        titleId="catalog-entry-title"
        description={description}
        metaItems={buildCatalogEntryHeroMetaItems(entry, detailProfile)}
        localizedContentLang={scopedLocale.isEnabled
          ? scopedLocale.contentLang
          : undefined}
        actionLabel={primaryAction.label}
        actionDisabled={primaryAction.disabled}
        secondaryActionContent={
          contextualFacilitationConfiguration ? (
            <section
              aria-labelledby="catalog-detail-use-with-others-title"
              className="catalog-detail__contextual-action"
            >
              <div className="catalog-detail__contextual-action-header">
                <ApplicationSectionHeader
                  title="Use with others"
                  titleId="catalog-detail-use-with-others-title"
                  variant="compact"
                  actions={(
                    <ApplicationContextualHelp
                      label="About using this activity with others"
                      title="About using this activity with others"
                    >
                      <p>
                        Use with others lets you run this activity as
                        a live session with participants in real
                        time. Start the session here and share the
                        session code with participants. They can join
                        from <Link to="/app/participate">Participate</Link>
                        {" "}→ Join a live session.
                      </p>
                    </ApplicationContextualHelp>
                  )}
                />
                <ApplicationButton
                  variant="link"
                  className="catalog-detail__secondary-action-button"
                  onClick={handleFacilitateActivity}
                >
                  Start a live session →
                </ApplicationButton>
              </div>
            </section>
          ) : null
        }
        actionControls={(
          <>
            <CatalogEntryAdaptationSelector
              entry={entry}
              selectedAdaptationId={selectedCAPEVAdaptationId}
              onChange={setSelectedCAPEVAdaptationId}
              onOpenRelatedEntry={(relatedSlug) =>
                navigate(`/app/catalog/${relatedSlug}`)
              }
            />
            <CatalogEntryLaunchConfigurationSelector
              entry={entry}
              selectedLaunchConfigurationId={
                selectedOrdinaryConfiguration?.id
              }
              availableLaunchConfigurations={
                selfDirectedConfigurations
              }
              showPreferenceControl
              onChange={(nextLaunchConfigurationId) => {
                const nextParams =
                  new URLSearchParams(searchParams);
                nextParams.set("launchConfig", nextLaunchConfigurationId);
                setSearchParams(nextParams, { replace: true });
              }}
            />
            <CatalogEntryProgramLocaleSelector
              id={`catalog-program-locale-${entry.slug}`}
            />
          </>
        )}
        onPrimaryAction={handlePrimaryAction}
      />

      {isProjectAccessCodeEnrollmentEntry(entry) ? (
        <JoinProjectWithCode
          accessCodeInputId="catalog-project-access-code-input"
          headingLevel={2}
          sectionId="catalog-project-access-code"
          title="Join this project"
          titleId="catalog-project-access-title"
        />
      ) : null}

      <CatalogEntryInformationPanels
        entry={entry}
        selectedLaunchConfiguration={selectedOrdinaryConfiguration}
        className="catalog-detail__info-grid"
      />
      <section className="catalog-detail__info-grid">
        {detailProfile.panels.relatedEntries && (
          <CatalogEntryRelatedEntriesPanel
            relatedEntries={relatedEntries}
            onSelectRelatedEntry={(relatedEntry) =>
              navigate(`/app/catalog/${relatedEntry.slug}`)
            }
          />
        )}
        {detailProfile.panels.contact && (
          <CatalogEntryContactPanel contactEmail={contactEmail} />
        )}
        {detailProfile.panels.links && (
          <CatalogEntryLinksPanel externalLinks={externalLinks} />
        )}
      </section>

      <ContactFormModal
        isOpen={Boolean(catalogContactRequest)}
        onRequestClose={() => setCatalogContactRequest(null)}
        initialMessage={buildCatalogContactMessage(
          catalogContactRequest?.entry,
          catalogContactRequest?.action
        )}
      />
    </ApplicationPage>
  );
}
